import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Check, ArrowRight, Sparkles, ShieldCheck, Crown, Mail, Gift,
} from "lucide-react";

/**
 * Pricing page — public, no auth required.
 *
 * Three plans (Insight 19$, Guardian 49$ "Recommended", Sentinel 99$).
 * Each card CTA is a mailto:upgrade@qenva.com link — there's no Stripe
 * checkout yet (the no-Stripe phase is intentional during the trial period).
 *
 * Visual system: QENVA Deep Navy gradient header, Electric Blue accents on
 * the recommended plan, Guardian Green for unique-to-Sentinel features.
 */
const PLANS = [
  {
    id: "insight",
    nameKey: "pricing.insight.name",
    priceKey: "pricing.insight.price",
    annualPriceKey: "pricing.insight.annual_price",
    originalPriceKey: "pricing.insight.original_price",
    originalAnnualPriceKey: "pricing.insight.original_annual_price",
    taglineKey: "pricing.insight.tagline",
    featuresKey: "pricing.insight.features",
    Icon: Sparkles,
    accent: "text-zinc-500",
    badge: null,
    cardBorder: "border-zinc-200",
    headerBg: "bg-white",
    iconBg: "bg-zinc-100",
    iconColor: "text-zinc-700",
    ctaClasses: "bg-zinc-900 hover:bg-zinc-800 text-white",
  },
  {
    id: "guardian",
    nameKey: "pricing.guardian.name",
    priceKey: "pricing.guardian.price",
    annualPriceKey: "pricing.guardian.annual_price",
    originalPriceKey: "pricing.guardian.original_price",
    originalAnnualPriceKey: "pricing.guardian.original_annual_price",
    taglineKey: "pricing.guardian.tagline",
    featuresKey: "pricing.guardian.features",
    Icon: ShieldCheck,
    accent: "text-qenva-cat-financing",
    badge: "pricing.guardian.badge_label",
    cardBorder: "border-qenva-blue/50 ring-2 ring-qenva-blue/15",
    headerBg: "bg-qenva-blue/5",
    iconBg: "bg-qenva-blue/15",
    iconColor: "text-qenva-blue",
    ctaClasses: "bg-qenva-blue hover:bg-qenva-blue/90 text-white",
  },
  {
    id: "sentinel",
    nameKey: "pricing.sentinel.name",
    priceKey: "pricing.sentinel.price",
    annualPriceKey: "pricing.sentinel.annual_price",
    originalPriceKey: "pricing.sentinel.original_price",
    originalAnnualPriceKey: "pricing.sentinel.original_annual_price",
    taglineKey: "pricing.sentinel.tagline",
    featuresKey: "pricing.sentinel.features",
    Icon: Crown,
    accent: "text-qenva-green",
    badge: null,
    cardBorder: "border-qenva-green/40",
    headerBg: "bg-qenva-green/5",
    iconBg: "bg-qenva-green/15",
    iconColor: "text-qenva-green",
    ctaClasses: "bg-qenva-navy hover:bg-qenva-navy-soft text-white",
  },
];

export default function Pricing() {
  const { t } = useTranslation("common");
  // Iter76 — monthly/annual toggle. Annual = 10 months only (2 months offered).
  const [billing, setBilling] = useState("monthly");
  const isAnnual = billing === "annual";

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="pricing-page">
      {/* Top header band — Deep Navy */}
      <header className="bg-qenva-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-8 flex items-center justify-between">
          <Link to="/" data-testid="pricing-brand-link" className="flex items-center">
            <img
              src="/brand/v2-dark-lockup.png"
              alt="QENVA · The Silent Guardian"
              height={48}
              draggable={false}
              style={{
                height: 48,
                width: "auto",
                objectFit: "contain",
                display: "block",
                userSelect: "none",
                border: "none",
                outline: "none",
                background: "transparent",
                boxShadow: "none",
              }}
              data-testid="pricing-header-lockup"
            />
          </Link>
          <Link
            to="/login"
            data-testid="pricing-login-link"
            className="text-[13px] text-white/80 hover:text-white inline-flex items-center gap-1.5"
          >
            {t("pricing.go_to_app")} <ArrowRight size={13} />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-14 pb-10 text-center">
        <p className="text-[11.5px] uppercase tracking-[0.22em] font-bold text-qenva-blue mb-3">
          {t("pricing.kicker")}
        </p>
        <h1
          className="text-4xl sm:text-5xl font-bold text-qenva-navy leading-[1.1] mb-4"
          style={{ fontFamily: "'Bodoni Moda', 'Playfair Display', Georgia, serif", fontWeight: 500, letterSpacing: "0.02em" }}
        >
          {t("pricing.headline")}
        </h1>
        <p className="text-[15px] text-zinc-600 leading-relaxed">
          {t("pricing.subhead")}
        </p>
      </section>

      {/* Plans grid */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        {/* Iter76 — Monthly / Annual toggle */}
        <div
          className="flex items-center justify-center mb-8"
          data-testid="pricing-billing-toggle"
        >
          <div
            role="tablist"
            aria-label={t("pricing.billing_toggle_aria")}
            className="inline-flex items-center gap-1 p-1 rounded-full bg-white border border-zinc-200 shadow-sm"
          >
            <button
              role="tab"
              type="button"
              aria-selected={!isAnnual}
              onClick={() => setBilling("monthly")}
              data-testid="pricing-toggle-monthly"
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors ${
                !isAnnual
                  ? "bg-qenva-navy text-white"
                  : "text-zinc-700 hover:text-zinc-950"
              }`}
            >
              {t("pricing.billing_monthly")}
            </button>
            <button
              role="tab"
              type="button"
              aria-selected={isAnnual}
              onClick={() => setBilling("annual")}
              data-testid="pricing-toggle-annual"
              className={`px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors inline-flex items-center gap-1.5 ${
                isAnnual
                  ? "bg-qenva-navy text-white"
                  : "text-zinc-700 hover:text-zinc-950"
              }`}
            >
              {t("pricing.billing_annual")}
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  isAnnual
                    ? "bg-qenva-green/25 text-white"
                    : "bg-qenva-green/15 text-qenva-green"
                }`}
                data-testid="pricing-annual-savings-inline"
              >
                {t("pricing.billing_two_months_free_short")}
              </span>
            </button>
          </div>
        </div>

        {isAnnual && (
          <div
            className="flex items-center justify-center gap-2 mb-6"
            data-testid="pricing-annual-badge"
          >
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-qenva-green/10 text-qenva-green text-[11.5px] font-semibold border border-qenva-green/30">
              <Gift size={12} strokeWidth={2.2} />
              {t("pricing.billing_two_months_free")}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const features = t(plan.featuresKey, { returnObjects: true }) || [];
            const priceLabel = isAnnual
              ? t(plan.annualPriceKey)
              : t(plan.priceKey);
            const originalPriceLabel = isAnnual
              ? t(plan.originalAnnualPriceKey)
              : t(plan.originalPriceKey);
            const suffixKey = isAnnual
              ? "pricing.per_year_suffix"
              : "pricing.per_month_suffix";
            const freqLabel = isAnnual
              ? t("pricing.billing_annual")
              : t("pricing.billing_monthly");
            const mailtoSubject = encodeURIComponent(
              t("pricing.email_subject_with_freq", {
                plan: t(plan.nameKey),
                freq: freqLabel,
                defaultValue: t("pricing.email_subject", { plan: t(plan.nameKey) }),
              }),
            );
            const mailto = `mailto:upgrade@qenva.com?subject=${mailtoSubject}`;
            const Icon = plan.Icon;
            return (
              <article
                key={plan.id}
                data-testid={`plan-card-${plan.id}`}
                className={`relative bg-white border-2 ${plan.cardBorder} rounded-2xl overflow-hidden flex flex-col`}
              >
                {/* Recommended ribbon */}
                {plan.badge && (
                  <div className="absolute top-0 right-0">
                    <span
                      className="inline-block px-3 py-1 rounded-bl-lg text-[10.5px] font-bold uppercase tracking-[0.16em] bg-qenva-blue text-white"
                      data-testid={`plan-badge-${plan.id}`}
                    >
                      {t(plan.badge)}
                    </span>
                  </div>
                )}
                {/* Header */}
                <div className={`${plan.headerBg} px-6 pt-7 pb-5 border-b border-zinc-100`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-xl ${plan.iconBg} grid place-items-center`}>
                      <Icon size={18} className={plan.iconColor} strokeWidth={1.75} />
                    </div>
                    <h3 className="text-[16px] font-bold text-qenva-navy">
                      {t(plan.nameKey)}
                    </h3>
                  </div>
                  <p className="text-[12.5px] text-zinc-600 leading-relaxed mb-4 min-h-[36px]">
                    {t(plan.taglineKey)}
                  </p>
                  <div className="flex items-center gap-2 mb-1" data-testid={`plan-launch-badge-${plan.id}`}>
                    <span
                      className="text-[13px] text-zinc-400 line-through"
                      data-testid={`plan-original-price-${plan.id}`}
                    >
                      {originalPriceLabel}
                    </span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-qenva-green/15 text-qenva-green text-[10px] font-bold uppercase tracking-wide">
                      {t("pricing.launch_price_badge")}
                    </span>
                  </div>
                  <p
                    className="text-[28px] font-bold text-qenva-navy"
                    data-testid={`plan-price-${plan.id}`}
                  >
                    {priceLabel}
                    <span className="text-[12.5px] font-normal text-zinc-500 ml-1">
                      {t(suffixKey)}
                    </span>
                  </p>
                </div>

                {/* Features */}
                <div className="px-6 py-5 flex-1">
                  <ul className="space-y-2.5">
                    {Array.isArray(features) && features.map((f, idx) => (
                      <li
                        key={idx}
                        className="flex items-start gap-2 text-[13px] text-zinc-700 leading-snug"
                      >
                        <Check size={13} className={`${plan.accent} shrink-0 mt-1`} strokeWidth={2.5} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="px-6 pb-6 pt-2">
                  <a
                    href={mailto}
                    data-testid={`plan-cta-${plan.id}`}
                    className={`w-full h-11 rounded-lg ${plan.ctaClasses} text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-colors`}
                  >
                    <Mail size={14} />
                    {t("pricing.cta")}
                    <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>

        {/* Compare row */}
        <p className="text-center text-[12.5px] text-zinc-500 mt-10">
          {t("pricing.footer_note")}
        </p>
      </section>

      <footer className="border-t border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11.5px] text-zinc-500">
          <p>© {new Date().getFullYear()} QENVA — The Silent Guardian™</p>
          <Link to="/login" className="hover:text-qenva-navy">
            {t("pricing.back_to_app")}
          </Link>
        </div>
      </footer>
    </div>
  );
}
